import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, CreditCard, XCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { EmptyState } from '@/components/EmptyState';
import { MoneyAmount } from '@/components/MoneyAmount';
import { Pagination } from '@/components/Pagination';
import { ApiError } from '@/lib/fetcher';
import {
  accountKey,
  accountLedgerKey,
  billingPacksKey,
  createCheckout,
  fetchAccount,
  fetchAccountLedger,
  fetchBillingPacks,
} from '@/lib/queries';
import { Receipt } from 'lucide-react';

/**
 * Billing tab — relaystation-v3-native Stripe Checkout redirect flow.
 *
 * T.B.6 (brief 49) — replaces the prior iframe-to-Relaystation widget.
 * Customer-facing flow:
 *
 *   1. Page loads → `GET /v1/billing/packs` returns the configured
 *      preset packs + custom-amount bounds.
 *   2. Customer clicks `$5`, `$20`, or enters a custom amount.
 *   3. Click "Top up" → `POST /v1/billing/checkout` returns
 *      `{ checkoutUrl }` → we `window.location = checkoutUrl`.
 *   4. Customer pays on Stripe.
 *   5. Stripe redirects back to `successUrl` (this page with
 *      `?session_id=...`) or `cancelUrl` (this page with `?cancel=1`).
 *   6. On success-return: refetch `/v1/account` so the new balance
 *      shows. The actual credit lands via the `webhook-stripe`
 *      Lambda → `stripeRail.onCheckoutSessionCompleted` → atomic
 *      ledger write + balance bump. The webhook usually arrives
 *      within seconds; we surface a "Top-up received" banner once
 *      the balance increments.
 *
 * No iframe, no Stripe Elements — pure redirect to a Stripe-hosted
 * checkout page. The Stripe key + Stripe checkout UX live entirely
 * server-side; the SPA only handles the customer's intent capture +
 * redirect.
 */

const SUCCESS_URL_BASE = `${window.location.origin}/billing?status=success`;
const CANCEL_URL_BASE = `${window.location.origin}/billing?status=cancel`;

function formatUsdFromMicros(microsStr: string): string {
  const micros = BigInt(microsStr);
  const dollars = micros / 1_000_000n;
  const remainder = micros % 1_000_000n;
  if (remainder === 0n) return `$${dollars}`;
  // 6dp precision; trim trailing zeros, pad to ≥ 2 decimals.
  let frac = remainder.toString().padStart(6, '0').replace(/0+$/, '');
  if (frac.length < 2) frac = frac.padEnd(2, '0');
  return `$${dollars}.${frac}`;
}

export function Billing() {
  const [searchParams, setSearchParams] = useSearchParams();
  const qc = useQueryClient();
  const status = searchParams.get('status');

  const accountQ = useQuery({ queryKey: accountKey, queryFn: fetchAccount });
  const packsQ = useQuery({
    queryKey: billingPacksKey,
    queryFn: fetchBillingPacks,
  });
  const [ledgerPage, setLedgerPage] = React.useState({ limit: 20, offset: 0 });
  const ledgerQ = useQuery({
    queryKey: accountLedgerKey(ledgerPage),
    queryFn: () => fetchAccountLedger(ledgerPage),
  });

  const [customAmount, setCustomAmount] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  const checkout = useMutation({
    mutationFn: createCheckout,
    onMutate: () => {
      setError(null);
    },
    onSuccess: (data) => {
      // Hand off to Stripe. The SPA's state is discarded on navigation,
      // which is fine — the success-return path re-mounts the page and
      // re-fetches /v1/account to pick up the new balance.
      window.location.href = data.checkoutUrl;
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message ?? err.code);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('unknown checkout error');
      }
    },
  });

  // On success-return, refetch /v1/account so the new balance shows
  // as soon as the webhook lands. The refetch is best-effort — if the
  // webhook hasn't arrived yet (slow Stripe → us round-trip), the
  // banner stays up; a manual refresh or the auto-refetch from React
  // Query's staleness will pick it up shortly.
  React.useEffect(() => {
    if (status === 'success') {
      void qc.invalidateQueries({ queryKey: accountKey });
    }
  }, [status, qc]);

  function clearStatusParam() {
    const next = new URLSearchParams(searchParams);
    next.delete('status');
    next.delete('session_id');
    setSearchParams(next, { replace: true });
  }

  function onPickPack(packId: string) {
    setError(null);
    checkout.mutate({
      packId,
      successUrl: SUCCESS_URL_BASE,
      cancelUrl: CANCEL_URL_BASE,
    });
  }

  function onCustomTopUp(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!packsQ.data) return;
    const trimmed = customAmount.trim();
    if (!trimmed) {
      setError('Enter an amount in USD.');
      return;
    }
    // Allow either "5" or "5.00" or "12.34".
    const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(trimmed);
    if (!match) {
      setError('Amount must be a dollar value with up to 2 decimals (e.g., 12.34).');
      return;
    }
    const dollarsPart = BigInt(match[1] ?? '0');
    const centsPart = BigInt((match[2] ?? '').padEnd(2, '0').slice(0, 2));
    const amountMicros = (dollarsPart * 100n + centsPart) * 10_000n;
    if (amountMicros <= 0n) {
      setError('Amount must be greater than zero.');
      return;
    }
    // Brief 257 — enforce the server's top-up range ($5–$100) client-side too,
    // so the customer gets a clear message before the round-trip. The bounds
    // come from /v1/billing/packs (the same admin_config the server guard reads),
    // so the message can't drift from the real limits.
    const minMicros = BigInt(packsQ.data.customAmount.minMicros);
    const maxMicros = BigInt(packsQ.data.customAmount.maxMicros);
    if (amountMicros < minMicros || amountMicros > maxMicros) {
      setError(
        `Enter an amount between ${formatUsdFromMicros(
          packsQ.data.customAmount.minMicros,
        )} and ${formatUsdFromMicros(packsQ.data.customAmount.maxMicros)}.`,
      );
      return;
    }
    checkout.mutate({
      customAmountMicros: amountMicros.toString(),
      successUrl: SUCCESS_URL_BASE,
      cancelUrl: CANCEL_URL_BASE,
    });
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Billing</h1>
        <p className="text-muted-foreground">
          Top up your balance. Relaystation never sees your card — Stripe
          handles payment entry on their own checkout page.
        </p>
      </header>

      {status === 'success' && (
        <Alert>
          <CheckCircle2 className="h-5 w-5" />
          <AlertTitle>Top-up received</AlertTitle>
          <AlertDescription className="flex items-center gap-3">
            <span>
              Your balance should update within a few seconds. If it
              doesn't, refresh this page.
            </span>
            <Button size="sm" variant="outline" onClick={clearStatusParam}>
              Dismiss
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {status === 'cancel' && (
        <Alert variant="destructive">
          <XCircle className="h-5 w-5" />
          <AlertTitle>Top-up cancelled</AlertTitle>
          <AlertDescription className="flex items-center gap-3">
            <span>No charge was made. You can try again below.</span>
            <Button size="sm" variant="outline" onClick={clearStatusParam}>
              Dismiss
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Balance
          </CardTitle>
          <CardDescription>
            Available for paid API calls.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {accountQ.isPending ? (
            <Skeleton className="h-8 w-32" />
          ) : accountQ.error ? (
            <p className="text-sm text-destructive">
              Couldn't load balance (
              {accountQ.error instanceof ApiError
                ? accountQ.error.code
                : 'unknown'}
              )
            </p>
          ) : (
            <div className="text-3xl font-semibold tabular-nums">
              {accountQ.data.balanceFormatted ?? accountQ.data.balance}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Top up</CardTitle>
          <CardDescription>
            Pick a preset or enter a custom amount. You'll be redirected
            to Stripe's checkout page to pay.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {packsQ.isPending ? (
            <div className="flex gap-2">
              <Skeleton className="h-10 w-24" />
              <Skeleton className="h-10 w-24" />
            </div>
          ) : packsQ.error ? (
            <p className="text-sm text-destructive">
              Couldn't load pack list.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {packsQ.data.packs.map((pack) => (
                <Button
                  key={pack.id}
                  variant="outline"
                  disabled={checkout.isPending}
                  onClick={() => onPickPack(pack.id)}
                >
                  {pack.label}
                </Button>
              ))}
            </div>
          )}

          <form
            onSubmit={onCustomTopUp}
            className="flex flex-wrap items-end gap-2"
          >
            <div className="flex-1 min-w-[180px]">
              <label
                htmlFor="customAmount"
                className="block text-sm font-medium mb-1"
              >
                Custom amount (USD)
              </label>
              <div className="flex items-center gap-1">
                <span className="text-muted-foreground">$</span>
                <Input
                  id="customAmount"
                  type="text"
                  inputMode="decimal"
                  placeholder="12.34"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  disabled={checkout.isPending}
                />
              </div>
              {packsQ.data && (
                <p className="text-xs text-muted-foreground mt-1">
                  Between {formatUsdFromMicros(packsQ.data.customAmount.minMicros)}{' '}
                  and {formatUsdFromMicros(packsQ.data.customAmount.maxMicros)}.
                </p>
              )}
            </div>
            <Button type="submit" disabled={checkout.isPending}>
              {checkout.isPending ? 'Redirecting…' : 'Top up'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Transactions
          </CardTitle>
          <CardDescription>
            Every credit and debit — top-ups, baton provisions, witnesses.
            Newest first.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {ledgerQ.isPending ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : ledgerQ.error ? (
            <Alert variant="destructive">
              <AlertTitle>
                Couldn't load transactions (
                {ledgerQ.error instanceof ApiError ? ledgerQ.error.code : 'unknown'})
              </AlertTitle>
            </Alert>
          ) : ledgerQ.data && ledgerQ.data.total === 0 ? (
            <EmptyState
              icon={Receipt}
              title="No transactions yet"
              description="Top up or provision a baton — your ledger entries appear here."
            />
          ) : ledgerQ.data ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ledgerQ.data.data.map((row) => {
                    const credit = row.direction === 'credit';
                    return (
                      <TableRow key={row.id}>
                        <TableCell className="whitespace-nowrap text-muted-foreground">
                          {new Date(row.createdAt).toLocaleString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </TableCell>
                        <TableCell>
                          <Badge variant={credit ? 'success' : 'secondary'}>
                            {row.direction}
                          </Badge>
                          <div className="text-xs text-muted-foreground">
                            {row.reason || row.serviceKey || '—'}
                          </div>
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-muted-foreground">
                          {row.source}
                        </TableCell>
                        <TableCell
                          className={`text-right font-mono tabular-nums ${
                            credit ? 'text-green-600' : ''
                          }`}
                        >
                          {credit ? '+' : '−'}
                          <MoneyAmount micros={row.amountMicros} minorDigits={4} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <div className="mt-3">
                <Pagination
                  total={ledgerQ.data.total}
                  limit={ledgerPage.limit}
                  offset={ledgerPage.offset}
                  onChange={setLedgerPage}
                />
              </div>
            </>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
